<script>
    import { browser } from '$app/environment';
    import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte';
    import { fly } from 'svelte/transition';
    import Icon from './Icon.svelte';
    import Button from './Button.svelte';
    import { onNavigate } from '$app/navigation';
    import {
        is_top_modal,
        register_modal,
        unregister_modal
    } from '$lib/utils/modal_state';

    export let xs = false;
    export let sm = false;
    // export let lg = false;
    // export let xl = false;

    export let collapsible = true;

    let modalContent;
    let modalTarget;
    let is_destroyed = false;
    let is_registered = false;

    const handle_escape = (e) => {
        if (e.key === 'Escape') {
            handleClose();
        }
    };

    onMount(async () => {
        if (!browser) {
            return;
        }

        await tick();

        if (is_destroyed || !(modalContent instanceof HTMLElement)) {
            return;
        }

        modalTarget = document.querySelector('#modal-target');

        if (!(modalTarget instanceof HTMLElement)) {
            return;
        }

        is_registered = true;
        modalTarget.appendChild(modalContent);
        register_modal(modalContent);

        document.addEventListener('keydown', handle_escape);
    });

    onDestroy(() => {
        is_destroyed = true;

        if (!browser) {
            return;
        }

        document.removeEventListener('keydown', handle_escape);

        if (!is_registered) {
            return;
        }

        if (
            modalContent instanceof HTMLElement &&
            modalTarget instanceof HTMLElement &&
            modalContent.parentNode === modalTarget
        ) {
            modalTarget.removeChild(modalContent);
        }

        unregister_modal(modalContent);
        is_registered = false;
    });

    onNavigate(async () => {
        if (is_registered) {
            unregister_modal(modalContent);
            is_registered = false;
        }

        if (
            modalContent instanceof HTMLElement &&
            modalTarget instanceof HTMLElement &&
            modalContent.parentNode === modalTarget
        ) {
            modalTarget.removeChild(modalContent);
        }

        dispatch('close');
        await tick();
    });

    let dispatch = createEventDispatcher();

    function handleClose() {
        if (!collapsible) {
            return;
        }

        if (is_top_modal(modalContent)) {
            dispatch('close');
        }
    }
</script>

<div
    class="w-full h-[100dvh] absolute bg-black/70 dark:bg-black/90"
    bind:this={modalContent}
    transition:fly|local={{ y: 100, duration: 200 }}
    aria-modal="true"
>
    <div
        class="
            modal
            absolute bottom-0 left-1/2 -translate-x-1/2


            max-h-[calc(100vh_-_calc(env(safe-area-inset-top)_+_20rem))]

            w-full
            flex flex-col
            rounded-t-3xl

            bg-surface-invert-main
            text-symbols-invert-default

            pb-safe

            {xs ? 'max-w-sm' : sm ? 'max-w-md' : 'max-w-xl'}

            sm:bottom-auto
            sm:top-1/2 sm:-translate-y-1/2
            sm:rounded-b-3xl
        "
    >
        {#if collapsible}
            <Button
                text
                round
                aria_label="Close modal"
                on:click={handleClose}
                class="absolute right-2 top-2 z-10"
            >
                <Icon times class="drop-shadow" />
            </Button>
        {/if}

        {#if $$slots.header}
            <div class="p-4 pb-0 {collapsible ? 'pr-16' : ''}">
                <slot name="header" />
            </div>
        {/if}

        <div class="p-4 flex-1 overflow-y-auto">
            <slot />
        </div>

        {#if $$slots.footer}
            <div class="p-4 border-t border-border-invert-muted">
                <slot name="footer" />
            </div>
        {/if}
    </div>
</div>

<style>
    .modal {
        max-height: calc(100dvh - calc(env(safe-area-inset-top) + 4rem));
        min-height: 30%;
    }

    /* :global(body:has(.modal)) {
        overflow: hidden;
    } */

    .modal.max-w-sm,
    .modal.max-w-md {
        min-height: 0;
    }
</style>
